import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "@mtm/components";
import { useLocation, useHistory } from "react-router-dom";
import "antd/dist/antd.css";
import Styles from "./Welcome.module.scss";
import CommonBgStyles from "../../BackgroundCircles.module.scss";
import backBtnStyles from "../Email/Email.module.scss";
import {
  GetRegistrationStatusDetails,
  UpdateCCPRegistrationDetails,
} from "../../services/RegistrationService";
import {
  GeneralDefinition,
  InsertParameter,
  RegistrationStatusDefaultValues,
  SearchPattern,
  ServicesTypesComponentLoad,
  TrackAnalytics,
} from "../../types/ApplicationTypes";
import imageMyself from "../../assets/images/MyselfImage.svg";
import imageSomeoneElse from "../../assets/images/SomeoneElseImage.svg";
import { notification } from "antd";
import { backArrowIcon } from "../../assets/images";
import { usePersonSummaryCtx } from "../../context/PersonSummaryContext";
import { useTripsCtx } from "../../context/TripDetailsContext";
import { useServiceUserCreationCtx } from "../../context/ServiceUserCreationContext";
import { usePersonCreation } from "../../hooks/usePersonCreation";

type Props = {
  setPageType: Function;
};

function Welcome({ setPageType }: Props) {
  const [maxServiceUserAddedMySelf, setMaxServiceUserAddedMySelf] = useState<
    boolean
  >(false);
  const [
    maxServiceUserAddedSomeoneElse,
    setMaxServiceUserAddedSomeoneElse,
  ] = useState<boolean>(false);
  const [isMember, setIsMember] =  useState<boolean>(false);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const { caregiverSummaryInfo, isLoadingPersonData } = usePersonSummaryCtx();
  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;
  const { whoWillUseServicesCtx } = useServiceUserCreationCtx();
  const [, setWhoWillUseServices] = whoWillUseServicesCtx;
  const {
    getMemberStatus,
  } = usePersonCreation();

  useEffect(() => {
    if (!isLoadingPersonData) {
      handlePasswordGenerated();
    }
  }, [isLoadingPersonData]);

  async function handlePasswordGenerated() {
    try {
      await handleFetchRegisteredUserId(caregiverSummaryInfo.wandaEmail);

      const reportingData = {
        email: caregiverSummaryInfo.wandaEmail,
        passwordGenerated: true,
        insertParameter: InsertParameter.passwordGenerated,
        personId: caregiverSummaryInfo.personId,
      };
      await UpdateCCPRegistrationDetails(reportingData);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to update ccp user details. Please try again later.",
      });
    }
  }

  const handleFetchRegisteredUserId = async (email: string) => {
    try {
      const isMemberStatus = await getMemberStatus(caregiverSummaryInfo.personId);
      setIsMember(isMemberStatus);

      const CCPRegisteredUserId = await GetRegistrationStatusDetails(
        caregiverSummaryInfo.wandaEmail,
        caregiverSummaryInfo.personId,
        4,
        RegistrationStatusDefaultValues.EmptyString
      );
      if (CCPRegisteredUserId.data === "") {
        const reportingData = {
          email: email,
          idpExists: false,
          personFound: isMemberStatus? true :false,
          personId: caregiverSummaryInfo.personId,
          insertParameter: InsertParameter.InsertRecordForMTMUser,
        };
        await UpdateCCPRegistrationDetails(reportingData);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to fetch registered userId. Please try again later.",
      });
    }
  };

  const displayMaxServiceUserAddedMessage = () => (
    <div className={Styles.maxAddTextSpanPosition}>
      <span className={Styles.maxAddText}>Max Service Users added.</span>
    </div>
  );

  async function handleContinueClicked(selectedFor: string) {
    if (serviceData.length === 25 && selectedFor === "Myself") {
      setMaxServiceUserAddedMySelf(true);
      return;
    }

    if (serviceData.length === 25 && selectedFor === "SomeoneElse") {
      setMaxServiceUserAddedSomeoneElse(true);
      return;
    }

    const isAddUserFromDashboard = pathname.includes(
      GeneralDefinition.WelcomePage
    );

    let addServiceUserFromDashboardFor =
      isAddUserFromDashboard && selectedFor === "SomeoneElse"
        ? "Profile"
        : "ProfileMyself";

    const whoWillUseServices = isAddUserFromDashboard
      ? addServiceUserFromDashboardFor
      : selectedFor;
    setWhoWillUseServices(whoWillUseServices);

    const personMatchStatus = await GetRegistrationStatusDetails(
      caregiverSummaryInfo.wandaEmail,
      caregiverSummaryInfo.personId,
      SearchPattern.PersonFound,
      GeneralDefinition.EmptyString
    );

    if (selectedFor === GeneralDefinition.Myself) {
      if (isMember && personMatchStatus.data === "Success") {
        setPageType(ServicesTypesComponentLoad.MTMBenefitCheck);
        setWhoWillUseServices(
          isAddUserFromDashboard ? "MemberProfile" : "Member"
        );
      } else {
        TrackAnalytics(
          "/Welcome",
          "Welcome Page - Myself Button Clicked",
          "Welcome Page"
        );
        setPageType(ServicesTypesComponentLoad.MyselfWillUseServices);
      }
    } else {
      TrackAnalytics(
        "/Welcome",
        "Welcome Page - Someone Else Button Clicked",
        "Welcome Page"
      );
      setPageType(ServicesTypesComponentLoad.SomeoneElseWillUseServices);
    }
  }

  const handleBack = () => {
    const source = query.get("source");
    const redirectTo = (pathName: string) => history.push(pathName);

    source === "Dashboard" && redirectTo("/DashBoard");
    source === "ScheduleService" && redirectTo("/DashBoard/ScheduleService");
    if (pathname.includes(GeneralDefinition.AddMaxServiceUser)) {
      redirectTo("/DashBoard");
    }
  };

  const shouldRenderBackButton =
    pathname.includes(GeneralDefinition.WelcomePage) ||
    (pathname.includes(GeneralDefinition.AddMaxServiceUser) &&
      serviceData.length === 25);

  return (
    <>
      <div>
        <div className={CommonBgStyles.bg_circle} />
        <Row>
          <Col span={2} lg={3} />
          <Col span={22} lg={21}>
            {shouldRenderBackButton && (
              <Row className={Styles.backBtnAlign}>
                <Col span={1} />
                <Col>
                  <Button
                    link={true}
                    clickHandler={handleBack}
                    className={backBtnStyles.backBtn}
                  >
                    <div>
                      <img src={backArrowIcon} alt="navigate-back" />
                      Back
                    </div>
                  </Button>
                </Col>
                <Col span={24} />
              </Row>
            )}
          </Col>
        </Row>

        <Row justify="center" gutter={[0, 50]}>
          <Col xs={2} lg={0} />
          <Col xs={20} lg={24} className={Styles.welcomeDescription}>
            Who will you be scheduling services for?
          </Col>
          <Col xs={2} lg={0} />
          <Col span={24} />
        </Row>

        <Row>
          <Col span={6} lg={4} xxl={6} />
          <Col span={24} lg={8} xxl={6}>
            <Col span={24} className={Styles.imgPosition}>
              <img src={imageSomeoneElse} className="welcomePageImage"></img>
            </Col>
            <Col span={24} className={Styles.btnPosition}>
              <Button
                className={Styles.buttonSomeoneElse}
                clickHandler={() =>
                  handleContinueClicked(GeneralDefinition.SomeoneElse)
                }
                disabled={maxServiceUserAddedSomeoneElse}
              >
                Someone Else
              </Button>
            </Col>

            <Col
              span={18}
              sm={24}
              md={8}
              lg={20}
              xxl={24}
              className={Styles.scheduleText}
            >
              <label className={Styles.welcomeText + " subTextFont "}>
                <p className={Styles.ptext}>I want to schedule and manage</p>
                services for a family member or friend.
              </label>
            </Col>
            <Col className={Styles.maxAddTextMargin}>
              {maxServiceUserAddedSomeoneElse &&
                displayMaxServiceUserAddedMessage()}
            </Col>
          </Col>

          <Col xs={24} lg={0}>
            <Row gutter={[0, 40]} justify="center">
              <Col span={24} />
              <Col span={20} className={Styles.dottedLine} />
              <Col span={24} />
            </Row>
          </Col>

          <Col span={24} lg={8} xxl={6}>
            <Col span={24} className={Styles.imgPosition}>
              <img src={imageMyself} className="welcomePageImage"></img>
            </Col>
            <Col span={24} className={Styles.btnPosition}>
              <Button
                className={Styles.buttonMyself}
                clickHandler={() =>
                  handleContinueClicked(GeneralDefinition.Myself)
                }
                disabled={maxServiceUserAddedMySelf}
              >
                Myself
              </Button>
            </Col>

            <Col
              span={16}
              sm={8}
              md={8}
              lg={14}
              xxl={18}
              className={Styles.scheduleText}
            >
              <label className={Styles.welcomeText + " subTextFont "}>
                <p>I want to schedule and manage services for myself.</p>
              </label>
            </Col>
            <Col>
              {maxServiceUserAddedMySelf && displayMaxServiceUserAddedMessage()}
            </Col>
          </Col>
          <Col span={6} lg={4} xxl={6} />
        </Row>
      </div>
    </>
  );
}
export default Welcome;
